import Styles from './LoadingPreview.module.scss'
import { CSSProperties } from 'react'
import { Skeleton } from 'antd'
import { LinkedinLoadingPreview } from './LinkedinLoadingPreview'
import { GenerationType } from 'components/BlazeCommandLine/api/createDocGenerationRequest'
import { DesignChannel } from 'components/NewFileModal/Types'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { ChannelIcon } from 'components/ChannelIcon/ChannelIcon'
import { Text } from 'blaze-ui/Text/Text'
import { getPostTypeName } from '../utils'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { ArrowLeft, Calendar1, Close, MoreDots } from 'blaze-ui/icons'
import { FacebookLoadingPreview } from './FacebookLoadingPreview'
import { XLoadingPreview } from './XLoadingPreview'
import { InstagramPostLoadingPreview } from './InstagramPostLoadingPreview'
import { InstagramStoryLoadingPreview } from './InstagramStoryLoadingPreview'
import { DesignLoadingPreview } from './DesignLoadingPreview'
import { ReelOrShortLoadingPreview } from './ReelOrShortLoadingPreview'
import { ArticleLoadingPreview } from './ArticleLoadingPreview'
import { Button } from 'blaze-ui/Button'
import { useIsScheduledPostsV3 } from 'hooks/isScheduledPostsV3'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'
import { SCHEDULED_POSTS_PATH } from 'paths'
import { useHistory, useRouteMatch } from 'react-router-dom'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { generatePath } from 'react-router-dom'
import { useAuth } from 'hooks/UseAuth'

const Header = ({
  channel,
  onClose,
}: {
  channel: ChannelType | DesignChannel | GenerationType | undefined
  onClose: () => void
}) => {
  const isSystemRedesignEnabled = useIsSystemRedesign()
  const postTypeName = getPostTypeName(channel)
  const isScheduledPostsV3 = useIsScheduledPostsV3()
  const isOnScheduledPosts = useRouteMatch(SCHEDULED_POSTS_PATH)
  const workspaceId = useCurrentWorkspaceId()
  const history = useHistory()
  const auth = useAuth()

  const handleCalendarButtonPress = () => {
    if (isOnScheduledPosts) {
      onClose()
    } else {
      onClose()
      const scheduledPostsPath = generatePath(SCHEDULED_POSTS_PATH, { workspaceId })
      history.push(scheduledPostsPath)
    }
  }

  if (isScheduledPostsV3) {
    return (
      <div className={Styles.loadingPreviewHeader}>
        <div className={Styles.headerLeft}>
          {isSystemRedesignEnabled ? (
            <>
              <IconButton icon={ArrowLeft} variant="secondary" onPress={onClose} data-testid="close-button" />
              <IconButton
                icon={Calendar1}
                variant="secondary"
                onPress={handleCalendarButtonPress}
                data-testid="calendar-button"
              />
            </>
          ) : (
            <Button
              variant="tertiary"
              frontIcon={ArrowLeft}
              rounded
              size="lg"
              onPress={onClose}
              data-testid="close-button"
            >
              Back
            </Button>
          )}
        </div>
        <div className={Styles.headerCenter}>
          <Skeleton avatar={false} paragraph={false} title={{ width: 320 }} />
          <IconButton icon={MoreDots} variant="tertiary" size="lg" isDisabled />
        </div>
        <div className={Styles.headerRight}>
          {auth && <Skeleton avatar={{ shape: 'circle', size: 32 }} title={false} active paragraph={false} />}
        </div>
      </div>
    )
  }

  return (
    <div className={Styles.loadingPreviewHeader}>
      <div className={Styles.headerLeft}>
        <ChannelIcon channel={channel} size={24} isTransparentBackground={false} />
        <Text variant="smallList" color="var(--dark-80)">
          {postTypeName}
        </Text>
        <Skeleton avatar={false} paragraph={false} title={{ width: 115 }} />
        <Skeleton avatar={{ size: 24 }} paragraph={false} title={false} />
      </div>
      <div className={Styles.headerCenter}>
        <Skeleton avatar={false} paragraph={false} title={{ width: 168 }} />
      </div>
      <div className={Styles.headerRight}>
        <Skeleton avatar={{ size: 24 }} paragraph={false} title={false} />
        <Skeleton avatar={false} paragraph={false} title={{ width: 215 }} />
        {auth && <IconButton icon={Close} variant="secondary" onPress={onClose} size="lg" data-testid="close-button" />}
      </div>
    </div>
  )
}

export const LoadingPreview = ({
  channel,
  onClose,
  width,
  height,
  assetType,
  color = '#96cbee4d',
}: {
  channel: ChannelType | DesignChannel | GenerationType | undefined | null
  onClose: () => void
  width?: number | null
  height?: number | null
  assetType?: string
  color?: string
}) => {
  const aspectRatio = width && height ? width / height : 1
  const styles = {
    '--brand-kit-color': color,
    '--aspect-ratio': aspectRatio,
  } as CSSProperties

  const isLinkedIn = channel === ChannelType.LinkedInPosts || channel === DesignChannel.LinkedInPost
  const isFacebook = channel === ChannelType.FacebookPost || channel === DesignChannel.FacebookPost
  const isTwitter = channel === ChannelType.Tweets || channel === DesignChannel.TwitterPost
  const isInstagram = channel === ChannelType.InstagramPosts || channel === DesignChannel.InstagramPost
  const isInstagramStory = channel === DesignChannel.InstagramStory
  const isVideoPortrait =
    channel === DesignChannel.InstagramReel ||
    channel === DesignChannel.TikTokPost ||
    channel === DesignChannel.YoutubeShort

  const isSocialPost = isLinkedIn || isFacebook || isTwitter || isInstagram || isInstagramStory || isVideoPortrait
  const isDesign = assetType === 'Design' && !isSocialPost
  const isArticle = assetType === 'Article' && !isSocialPost

  return (
    <div className={Styles.loadingPreviewContainer} style={isArticle ? undefined : styles}>
      <Header channel={channel ?? undefined} onClose={onClose} />
      <div className={Styles.loadingPreviewContent}>
        {isLinkedIn && <LinkedinLoadingPreview />}
        {isFacebook && <FacebookLoadingPreview />}
        {isTwitter && <XLoadingPreview />}
        {isInstagram && <InstagramPostLoadingPreview />}
        {isInstagramStory && <InstagramStoryLoadingPreview />}
        {isVideoPortrait && <ReelOrShortLoadingPreview />}
        {isDesign && <DesignLoadingPreview width={width || 1} height={height || 1} />}
        {isArticle && <ArticleLoadingPreview />}
      </div>
    </div>
  )
}
